<script>
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import dynamicField from '../dynamicField/Index.vue';
import { resolveVariablePath } from '../../helpers/variablePathResolver';

const STRING_FIELD_TYPES = [
  'text',
  'textarea',
  'richtext',
  'email',
  'url',
  'array',
];

export default {
  components: {
    DynamicField: dynamicField,
    ButtonNext,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    variables: {
      type: Array,
      default: () => [],
    },
    disableRemove: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['remove', 'change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      toLocale,
    };
  },
  data() {
    return {
      updatedValue: this.value,
    };
  },
  computed: {
    conditionRegistry() {
      return this.$store.getters['wgptAutomationRegistry/getAction']('branch');
    },
    conditionFields() {
      return this.conditionRegistry.condition_fields || [];
    },
    comparisonOperators() {
      return this.$store.getters[
        'wgptAutomationRegistry/getComparisonOperators'
      ];
    },
    variableField() {
      return {
        name: 'variable_name',
        type: 'variable',
        label: 'FORM.CONDITION_FIELDS.VARIABLE_NAME.LABEL',
        required: true,
      };
    },
    operatorField() {
      return {
        name: 'operator',
        type: 'select',
        label: 'FORM.CONDITION_FIELDS.OPERATOR.LABEL',
        required: true,
        options: this.comparisonOperators,
      };
    },
    valueField() {
      let type = this.selectedVariableField.type ?? 'text';
      if (STRING_FIELD_TYPES.includes(type)) {
        type = 'text';
      }

      return {
        ...this.selectedVariableField,
        type,
        required: true,
        name: 'value',
        label: 'FORM.CONDITION_FIELDS.VALUE.LABEL',
      };
    },
    selectedVariableField() {
      const selectedVariableFieldValue = this.updatedValue.variable_name;
      return this.getVariableField(selectedVariableFieldValue) ?? {};
    },
    selectedComparisonField() {
      const selectedComparisonFieldValue =
        this.updatedValue[this.operatorField.name];
      const comparisonField = this.comparisonOperators.find(
        ({ value }) => value === selectedComparisonFieldValue
      );
      return comparisonField || {};
    },
    showValueField() {
      return !this.selectedComparisonField.no_value;
    },
  },
  methods: {
    onChange(fieldName, value) {
      const updatedValueUpdates = {
        [fieldName]: value,
      };
      if (fieldName === this.variableField.name) {
        updatedValueUpdates[this.valueField.name] = '';
      }
      this.updatedValue = {
        ...this.updatedValue,
        ...updatedValueUpdates,
      };
      this.$emit('change', this.updatedValue);
    },
    getVariableField(variablePath) {
      const valueInOptions = this.variables.find(
        variable => !variable.wrapper && variable.name === variablePath
      );
      if (valueInOptions) {
        return {
          ...valueInOptions,
          label: this.toLocale(valueInOptions.label, valueInOptions.name),
          value: variablePath,
        };
      }

      // Dotted paths walk through wrappers; custom objects can nest other
      // custom objects (e.g. car_listing.make.name).
      const { status, chain } = resolveVariablePath(
        this.variables,
        variablePath,
        {
          getCustomObjects:
            this.$store.getters['wgptAutomationRegistry/getCustomObjects'],
          getDynamicOptions:
            this.$store.getters['wgptAutomationRegistry/getDynamicOptions'],
          toLocale: this.toLocale,
        }
      );
      if (status !== 'resolved' || chain.length < 2) return {};

      const field = chain[chain.length - 1];
      const label = chain.map(node => node.resolvedLabel).join('/');
      return { ...field, label, value: variablePath };
    },
  },
};
</script>

<template>
  <div class="flex gap-1">
    <div class="flex-1 grid grid-cols-3 gap-1.5 gap-y-6">
      <DynamicField
        :field="variableField"
        :value="updatedValue[variableField.name]"
        :variables="variables"
        :disabled="disabled"
        :dirty="dirty"
        @change="value => onChange(variableField.name, value)"
      />
      <DynamicField
        :class="{ 'col-span-2': !showValueField }"
        :field="operatorField"
        :value="updatedValue[operatorField.name]"
        :disabled="disabled"
        :dirty="dirty"
        @change="value => onChange(operatorField.name, value)"
      />
      <DynamicField
        v-if="showValueField"
        :field="valueField"
        :value="updatedValue[valueField.name]"
        :variables="variables"
        :disabled="disabled"
        :dirty="dirty"
        @change="value => onChange(valueField.name, value)"
      />

      <div class="col-start-2">
        <slot name="operator-field" />
      </div>
    </div>
    <ButtonNext
      size="xs"
      variant="ghost"
      icon="i-lucide-x"
      color="slate"
      class="shrink-0 mt-8"
      :disabled="disableRemove"
      @click="$emit('remove')"
    />
  </div>
</template>
